import type {
  AllFees,
  Amount,
  CostTotals,
  Fee,
  SelectedFeeOption,
  SelectedFeeOptions,
} from '@/src/types/costAndFees.type';
import { extractFeeGroups } from './costFeeUtils';

export type FeeFrequency = 'monthly' | 'moveIn';

export function getFeeSelectionKey(frequency: FeeFrequency, fee: Fee): string {
  return `${frequency}:${fee.type}:${fee.displayName}`;
}

export function getFeeSelection(
  frequency: FeeFrequency,
  fee: Fee,
  selectedFeeOptions: SelectedFeeOptions,
): SelectedFeeOption | undefined {
  return selectedFeeOptions[getFeeSelectionKey(frequency, fee)];
}

export function getFeeCounterValue(fee: Fee, selection: SelectedFeeOption | undefined, isOptional: boolean): number {
  if (!selection) return isOptional ? 0 : fee.counter.min;

  const minimum = isOptional ? Math.max(1, fee.counter.min) : fee.counter.min;
  const value = Math.max(minimum, Math.trunc(selection.counterValue));
  return fee.counter.max === null ? value : Math.min(value, Math.max(minimum, fee.counter.max));
}

function getSelectedAmount(fee: Fee, selection: SelectedFeeOption | undefined): Amount {
  if (!selection?.optionSelected) return fee.amount;
  return fee.options.find((option) => option.displayName === selection.optionSelected)?.amount ?? fee.amount;
}

export function multiplyAmount(amount: Amount, count: number): Amount {
  if ('displayText' in amount) return amount;
  return {
    min: amount.min * count,
    max: amount.max === null ? null : (amount.max ?? amount.min) * count,
  };
}

export function addAmounts(first: Amount | undefined, second: Amount | undefined): Amount {
  if (!first && !second) return { min: 0, max: 0 };
  if (!first) return second!;
  if (!second) return first;
  if ('displayText' in first) return first;
  if ('displayText' in second) return second;

  return {
    min: first.min + second.min,
    max: first.max === null || second.max === null ? null : (first.max ?? first.min) + (second.max ?? second.min),
  };
}

export function getAppliedFeeAmount(fee: Fee, selection: SelectedFeeOption | undefined, isOptional: boolean): Amount {
  return multiplyAmount(getSelectedAmount(fee, selection), getFeeCounterValue(fee, selection, isOptional));
}

function replaceDefaultContribution(base: Amount, defaultAmount: Amount, selectedAmount: Amount): Amount {
  if ('displayText' in base) return base;
  if ('displayText' in selectedAmount) return selectedAmount;
  if ('displayText' in defaultAmount) return defaultAmount;

  return {
    min: base.min + selectedAmount.min - defaultAmount.min,
    max:
      base.max === null || selectedAmount.max === null || defaultAmount.max === null
        ? null
        : (base.max ?? base.min) +
          (selectedAmount.max ?? selectedAmount.min) -
          (defaultAmount.max ?? defaultAmount.min),
  };
}

function calculateSectionEstimate(
  apiAmount: Amount,
  fees: AllFees[FeeFrequency],
  frequency: FeeFrequency,
  selectedFeeOptions: SelectedFeeOptions,
): Amount {
  const { required, optional } = extractFeeGroups(fees);
  let total = apiAmount;

  for (const fee of required) {
    const selection = getFeeSelection(frequency, fee, selectedFeeOptions);
    if (!selection) continue;
    total = replaceDefaultContribution(
      total,
      getAppliedFeeAmount(fee, undefined, false),
      getAppliedFeeAmount(fee, selection, false),
    );
  }

  for (const fee of optional) {
    const selection = getFeeSelection(frequency, fee, selectedFeeOptions);
    if (!selection) continue;
    total = addAmounts(total, getAppliedFeeAmount(fee, selection, true));
  }

  return total;
}

export function calculateEstimateAmounts(
  effectiveCosts: CostTotals | null | undefined,
  effectiveFees: AllFees | null | undefined,
  selectedFeeOptions: SelectedFeeOptions,
) {
  const monthly = effectiveCosts?.monthly?.amount ?? { min: 0, max: 0 };
  const moveIn = effectiveCosts?.moveIn?.amount ?? { min: 0, max: 0 };

  return {
    monthly: effectiveFees
      ? calculateSectionEstimate(monthly, effectiveFees.monthly, 'monthly', selectedFeeOptions)
      : monthly,
    moveIn: effectiveFees
      ? calculateSectionEstimate(moveIn, effectiveFees.moveIn, 'moveIn', selectedFeeOptions)
      : moveIn,
  };
}

export function calculateFeeSectionTotal(
  required: Array<Fee>,
  optional: Array<Fee>,
  frequency: FeeFrequency,
  selectedFeeOptions: SelectedFeeOptions,
): Amount {
  const requiredTotal = required.reduce(
    (total, fee) =>
      addAmounts(total, getAppliedFeeAmount(fee, getFeeSelection(frequency, fee, selectedFeeOptions), false)),
    undefined as Amount | undefined,
  ) ?? { min: 0, max: 0 };
  return optional.reduce((total, fee) => {
    const selection = getFeeSelection(frequency, fee, selectedFeeOptions);
    return selection ? addAmounts(total, getAppliedFeeAmount(fee, selection, true)) : total;
  }, requiredTotal);
}
